import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Shield, Leaf, Sparkles, ChevronDown, Star, Check,
  Truck, Eye, Zap, Package, Heart, BadgeCheck, Wallet, MapPin, Thermometer, Award,
} from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { reviews as staticReviews } from '../data/products';
import { useTheme } from '../context/ThemeContext';
import { useProducts } from '../context/ProductsContext';
import ProductCard from '../components/ProductCard';
import { CampaignHeroDecor, CampaignOfferChip } from '../components/CampaignHero';
import { reviews as reviewsApi, type ReviewAPI } from '../api/client';
import type { Product } from '../types';
import SEO from '../components/SEO';
import { pageSeoMeta } from '@shared/pageSeoMeta';

const brands = [
  { name: 'ZYN', color: '#1e40af', tagline: 'Clean nicotine, refined taste', image: '/uploads/brands/zyn.webp' },
  { name: 'VELO', color: '#0f766e', tagline: 'Smooth and fresh, everywhere', image: '/uploads/brands/velo.avif' },
  { name: 'VELO Shift', color: '#7c3aed', tagline: 'Premium, refined elegance', image: '/uploads/hq/velo-shift/velo-shift-cool-peppermint-8mg.webp' },
  { name: 'Pablo', color: '#991b1b', tagline: 'Extreme strength, bold flavors', image: '/uploads/brands/pablo.avif' },
  { name: 'White Fox', color: '#52525b', tagline: 'Pure and powerful', image: '/uploads/hq/whitefox/whitefox-full-charge.png' },
  { name: 'CLEW', color: '#c2410c', tagline: 'Accessible and easy', image: '/uploads/brands/clew.avif' },
];

// "How to Use" steps — shared by the desktop grid and the mobile swipe carousel.
const HOW_TO_STEPS = [
  {
    step: 1,
    title: 'Open & Place',
    desc: 'Take one pouch and place it between your gum and upper lip.',
    icon: <Package size={28} />,
    gradient: 'from-emerald-500 to-teal-400',
    bgGlow: 'emerald',
  },
  {
    step: 2,
    title: 'Feel the Rush',
    desc: 'Experience the clean nicotine release within just 5-10 minutes.',
    icon: <Zap size={28} />,
    gradient: 'from-blue-500 to-indigo-400',
    bgGlow: 'blue',
  },
  {
    step: 3,
    title: 'Dispose & Done',
    desc: 'After 20-40 minutes, dispose the pouch. No spit, no mess.',
    icon: <Check size={28} />,
    gradient: 'from-amber-500 to-orange-400',
    bgGlow: 'amber',
  },
];

/* =======================================
   Animated Counter Component
======================================= */
function AnimatedCounter({ end, suffix = '', duration = 2 }: { end: number; suffix?: string; duration?: number }) {
  // Start at `end` so the SSR'd HTML contains the real number (crawlers
  // see "180+" not "0+"). The animation effect resets to 0 and counts up
  // once the element enters the viewport on the client.
  const [count, setCount] = useState(end);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView || hasAnimated) return;
    setHasAnimated(true);
    setCount(0);
    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3); // easeOutCubic
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isInView, end, duration, hasAnimated]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* =======================================
   Can Animation Component (Generic)
======================================= */
function CanAnimation({ showcaseProducts }: { showcaseProducts: Product[] }) {
  const [phase, setPhase] = useState<'idle' | 'hover' | 'open'>('idle');
  const [currentIndex, setCurrentIndex] = useState(0);

  const safeLen = showcaseProducts.length || 1;
  const currentProduct = showcaseProducts[currentIndex % safeLen];

  // Start animation immediately on mount (component only mounts when products exist)
  useEffect(() => {
    if (showcaseProducts.length === 0) return;
    if (phase === 'idle') {
      const t1 = setTimeout(() => setPhase('hover'), 600);
      const t2 = setTimeout(() => setPhase('open'), 1800);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [phase, showcaseProducts.length]);

  // Auto-cycle through products
  useEffect(() => {
    if (showcaseProducts.length === 0) return;
    const interval = setInterval(() => {
      setPhase('idle');
      setCurrentIndex((prev) => (prev + 1) % showcaseProducts.length);
      setTimeout(() => setPhase('hover'), 600);
      setTimeout(() => setPhase('open'), 1800);
    }, 5000);
    return () => clearInterval(interval);
  }, [showcaseProducts.length]);

  if (!currentProduct) {
    return <div className="relative w-full max-w-sm h-96 mx-auto flex items-center justify-center" />;
  }

  return (
    <div className="relative w-full max-w-sm h-96 mx-auto flex items-center justify-center">
      {/* Background glow */}
      <motion.div
        className="absolute w-72 h-72 rounded-full pointer-events-none"
        animate={phase === 'open' ? {
          background: [`radial-gradient(circle, ${currentProduct.colorAccent}25 0%, transparent 70%)`, `radial-gradient(circle, ${currentProduct.colorAccent}40 0%, transparent 70%)`, `radial-gradient(circle, ${currentProduct.colorAccent}25 0%, transparent 70%)`],
          scale: [1, 1.15, 1],
        } : {
          background: `radial-gradient(circle, ${currentProduct.colorAccent}12 0%, transparent 70%)`,
        }}
        transition={{ duration: 3, repeat: Infinity }}
      />

      {/* Floating particles when open */}
      {phase === 'open' && [...Array(8)].map((_, i) => (
        <motion.div
          key={`particle-${currentIndex}-${i}`}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: currentProduct.colorAccent }}
          initial={{ opacity: 0, scale: 0, x: 0, y: 0 }}
          animate={{
            opacity: [0, 0.8, 0],
            scale: [0, 1, 0],
            x: Math.cos((i / 8) * Math.PI * 2) * (100 + i * 10),
            y: Math.sin((i / 8) * Math.PI * 2) * (100 + i * 10) - 30,
          }}
          transition={{ duration: 2 + i * 0.2, repeat: Infinity, delay: i * 0.25, ease: 'easeOut' }}
        />
      ))}

      {/* The can */}
      <motion.div
        className="relative z-10"
        key={`can-${currentIndex}`}
        animate={
          phase === 'idle'
            ? { y: 0, rotate: 0, scale: 1 }
            : phase === 'hover'
            ? { y: -10, rotate: -3, scale: 1.05 }
            : { y: -20, rotate: -8, scale: 1.08 }
        }
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
      >
        <img
          src={currentProduct.image}
          alt={currentProduct.name}
          className="w-56 h-56 object-contain drop-shadow-2xl"
        />

        {/* Sheen sweep across can */}
        <motion.div
          className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
          initial={{ opacity: 0 }}
          animate={phase !== 'idle' ? { opacity: 1 } : { opacity: 0 }}
        >
          <motion.div
            className="absolute inset-y-0 w-12 bg-gradient-to-r from-transparent via-white/25 to-transparent"
            initial={{ x: -60 }}
            animate={phase !== 'idle' ? { x: [- 60, 280] } : { x: -60 }}
            transition={{ duration: 1.2, delay: 0.3, ease: 'easeInOut' }}
          />
        </motion.div>
      </motion.div>

      {/* Pouch popping out */}
      <AnimatePresence>
        {phase === 'open' && (
          <motion.div
            className="absolute z-20"
            initial={{ y: 20, x: 40, scale: 0, opacity: 0, rotate: -20 }}
            animate={{ y: -100, x: 70, scale: 1, opacity: 1, rotate: 5 }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 15,
              delay: 0.1,
            }}
          >
            <div className="relative">
              {/* Pouch — oval pillow shape */}
              <div className="w-24 h-16 bg-gradient-to-br from-teal-50 via-white to-teal-100 rounded-[50%] shadow-2xl border border-teal-200/60 flex items-center justify-center backdrop-blur-sm">
                <span className="text-teal-700 font-extrabold text-xs tracking-widest">{currentProduct.brand}</span>
              </div>
              {/* Top sparkle */}
              <motion.div
                className="absolute -top-3 -right-3 text-yellow-400"
                animate={{ scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Sparkles size={18} />
              </motion.div>
              {/* Bottom sparkle */}
              <motion.div
                className="absolute -bottom-2 -left-4 text-emerald-400"
                animate={{ scale: [1, 1.4, 1], opacity: [0.4, 1, 0.4] }}
                transition={{ duration: 1.8, repeat: Infinity, delay: 0.4 }}
              >
                <Sparkles size={14} />
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Second pouch (staggered) */}
      <AnimatePresence>
        {phase === 'open' && (
          <motion.div
            className="absolute z-20"
            initial={{ y: 10, x: -30, scale: 0, opacity: 0, rotate: 15 }}
            animate={{ y: -70, x: -80, scale: 0.85, opacity: 0.9, rotate: -12 }}
            transition={{
              type: 'spring',
              stiffness: 250,
              damping: 18,
              delay: 0.5,
            }}
          >
            <div className="w-20 h-14 bg-gradient-to-br from-teal-50 via-white to-teal-100 rounded-[50%] shadow-xl border border-teal-200/40 flex items-center justify-center">
              <span className="text-teal-600 font-bold text-[9px] tracking-wider">{currentProduct.brand}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* "Open" label that appears */}
      <AnimatePresence>
        {phase === 'open' && (
          <motion.div
            className="absolute bottom-4 z-20"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
          >
            <span className="text-xs font-semibold text-emerald-500 bg-emerald-500/10 border border-emerald-500/20 px-4 py-1.5 rounded-full">
              Pop. Place. Enjoy. ✨
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* =======================================
   Marquee Reviews Component
======================================= */
function MarqueeReviews() {
  const { isDark } = useTheme();
  const [apiReviews, setApiReviews] = useState<ReviewAPI[]>([]);

  useEffect(() => {
    reviewsApi.featured(20)
      .then(data => setApiReviews(data))
      .catch(() => { /* silent — will use static fallback */ });
  }, []);

  // Build a unified review list: real (API) reviews first, then top up with
  // curated static testimonials (skipping any that duplicate a real review's
  // text) so the marquee always looks full even when few real reviews exist
  // yet. As more real reviews accumulate, the static ones naturally drop off.
  const MIN_REVIEWS = 8;
  const displayReviews = useMemo(() => {
    const fromApi = apiReviews.map(r => {
      // Priority: display_name > anonymous > real user name
      let name: string;
      if (r.display_name) {
        name = r.display_name;
      } else if (r.is_anonymous) {
        name = 'Anonymous';
      } else {
        name = `${r.user.first_name} ${r.user.last_name?.charAt(0) || ''}.`;
      }
      return {
        id: r.id,
        name,
        location: '',
        rating: r.rating,
        text: r.text,
      };
    });

    const seen = new Set(fromApi.map(r => r.text.trim().toLowerCase()));
    const fromStatic = staticReviews
      .filter(r => !seen.has(r.text.trim().toLowerCase()))
      .map(r => ({
        id: r.id,
        name: r.name,
        location: r.location,
        rating: r.rating,
        text: r.text,
      }));

    return [...fromApi, ...fromStatic].slice(0, Math.max(MIN_REVIEWS, fromApi.length));
  }, [apiReviews]);

  const doubledReviews = [...displayReviews, ...displayReviews];

  return (
    <div className="relative overflow-hidden">
      {/* Fade edges */}
      <div className={`absolute left-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-r ${isDark ? 'from-gray-950' : 'from-white'} to-transparent`} />
      <div className={`absolute right-0 top-0 bottom-0 w-24 z-10 pointer-events-none bg-gradient-to-l ${isDark ? 'from-gray-950' : 'from-white'} to-transparent`} />

      <div className="flex animate-marquee hover:[animation-play-state:paused]">
        {doubledReviews.map((review, i) => (
          <div
            key={`${review.id}-${i}`}
            className={`flex-shrink-0 w-[280px] sm:w-[380px] mx-2 sm:mx-3 p-4 sm:p-6 rounded-2xl border transition-all hover:scale-[1.02] ${
              isDark
                ? 'bg-gray-900 border-gray-800 hover:border-emerald-800'
                : 'bg-white border-gray-100 hover:border-emerald-200 shadow-sm hover:shadow-lg'
            }`}
          >
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: review.rating }).map((_, j) => (
                <Star key={j} size={14} fill="#f59e0b" stroke="#f59e0b" />
              ))}
            </div>
            <p className={`text-sm leading-relaxed mb-4 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              &ldquo;{review.text}&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center">
                <span className="text-white font-bold text-sm">
                  {review.name.charAt(0)}
                </span>
              </div>
              <div>
                <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{review.name}</p>
                {review.location && <p className="text-xs text-gray-500">{review.location}</p>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* =======================================
   Floating Product Orbit (Hero)
======================================= */
function FloatingProductOrbit() {
  const orbitProducts = [
    { src: '/uploads/hq/zyn/zyn-cool-mint-11mg.png', alt: 'ZYN Cool Mint 11mg nicotine pouches', size: 'w-44 h-44' },
    { src: '/uploads/hq/velo/velo-icy-berries-10mg.png', alt: 'VELO Icy Berries 10mg nicotine pouches', size: 'w-36 h-36' },
    { src: '/uploads/hq/pablo/pablo-grape-ice-17mg.png', alt: 'Pablo Grape Ice 17mg nicotine pouches', size: 'w-40 h-40' },
    { src: '/uploads/hq/whitefox/whitefox-peppered-mint.png', alt: 'White Fox Peppered Mint nicotine pouches', size: 'w-32 h-32' },
    { src: '/uploads/hq/velo/velo-tropical-ice-10mg.png', alt: 'VELO Tropical Ice 10mg nicotine pouches', size: 'w-36 h-36' },
  ];

  return (
    <div className="relative w-full h-[500px] flex items-center justify-center">
      {/* Center glow */}
      <div className="absolute w-64 h-64 rounded-full bg-emerald-500/20 blur-3xl animate-pulse-slow" />

      {/* Orbit rings */}
      <motion.div
        className="absolute w-80 h-80 rounded-full border border-emerald-500/20"
        animate={{ rotate: 360 }}
        transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
      />
      <motion.div
        className="absolute w-[260px] h-[260px] sm:w-[420px] sm:h-[420px] rounded-full border border-dashed border-emerald-500/10"
        animate={{ rotate: -360 }}
        transition={{ duration: 50, repeat: Infinity, ease: 'linear' }}
      />

      {/* Center product */}
      <motion.div
        className="relative z-10"
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img
          src="/uploads/hq/velo/velo-freezing-peppermint-17mg.png"
          alt="VELO Freezing Peppermint"
          className="w-56 h-56 object-contain drop-shadow-[0_20px_50px_rgba(16,185,129,0.3)]"
        />
      </motion.div>

      {/* Orbiting products */}
      {orbitProducts.map((product, i) => {
        const angle = (i / orbitProducts.length) * 360;
        const radius = 180;
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const y = Math.sin((angle * Math.PI) / 180) * radius;

        return (
          <motion.div
            key={product.alt}
            className="absolute"
            initial={{ x, y, opacity: 0, scale: 0 }}
            animate={{
              x: [x, x + 20, x - 10, x],
              y: [y, y - 15, y + 10, y],
              opacity: 1,
              scale: 1,
            }}
            transition={{
              x: { duration: 6 + i, repeat: Infinity, ease: 'easeInOut' },
              y: { duration: 5 + i, repeat: Infinity, ease: 'easeInOut' },
              opacity: { duration: 0.8, delay: 0.5 + i * 0.15 },
              scale: { duration: 0.6, delay: 0.5 + i * 0.15, ease: [0.34, 1.56, 0.64, 1] },
            }}
          >
            <img
              src={product.src}
              alt={product.alt}
              className={`${product.size} object-contain drop-shadow-xl hover:scale-110 transition-transform duration-300 cursor-pointer`}
            />
          </motion.div>
        );
      })}
    </div>
  );
}

/* =======================================
   Brand Card (shared by the desktop grid + mobile marquee)
======================================= */
function BrandCard({ brand, isDark }: { brand: (typeof brands)[number]; isDark: boolean }) {
  return (
    <Link
      to={`/store?brand=${encodeURIComponent(brand.name)}`}
      className={`block p-6 rounded-2xl border-2 transition-all text-center group overflow-hidden relative h-full min-h-[200px] flex flex-col items-center justify-center ${
        isDark
          ? 'bg-gray-800 border-gray-700 hover:border-emerald-500'
          : 'bg-white border-gray-100 hover:border-emerald-500 shadow-sm hover:shadow-xl'
      }`}
    >
      {/* Shimmer overlay */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 animate-shimmer transition-opacity duration-300" />

      <div className="relative z-10 flex flex-col items-center">
        <div className="w-28 h-28 mx-auto rounded-2xl overflow-hidden mb-4 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center">
          <img src={brand.image} alt={brand.name} className="w-full h-full object-contain" />
        </div>
        <h3 className={`font-bold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{brand.name}</h3>
        <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{brand.tagline}</p>
      </div>
    </Link>
  );
}

/* =======================================
   Brands Marquee (mobile) — auto-scrolls continuously, but the user can also
   grab/swipe it by hand. Built on a real scroll container (native touch drag)
   with a rAF loop nudging scrollLeft; the brand list is duplicated so wrapping
   in either direction is seamless. Auto-scroll pauses while the user interacts.
======================================= */
function BrandsMarquee({ isDark }: { isDark: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;
    const step = () => {
      const half = el.scrollWidth / 2;
      if (half > 0) {
        if (!pausedRef.current && !reduce) el.scrollLeft += 0.6;
        // Keep position within the first copy so the loop is invisible.
        if (el.scrollLeft >= half) el.scrollLeft -= half;
        else if (el.scrollLeft < 0) el.scrollLeft += half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, []);

  const pause = () => {
    pausedRef.current = true;
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  };
  const resumeSoon = () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => { pausedRef.current = false; }, 1500);
  };

  return (
    <div className="sm:hidden relative overflow-hidden">
      {/* Fade edges */}
      <div className={`absolute left-0 top-0 bottom-0 w-12 z-10 pointer-events-none bg-gradient-to-r ${isDark ? 'from-gray-900' : 'from-gray-50'} to-transparent`} />
      <div className={`absolute right-0 top-0 bottom-0 w-12 z-10 pointer-events-none bg-gradient-to-l ${isDark ? 'from-gray-900' : 'from-gray-50'} to-transparent`} />
      <div
        ref={ref}
        onPointerDown={pause}
        onPointerUp={resumeSoon}
        onPointerCancel={resumeSoon}
        onTouchStart={pause}
        onTouchEnd={resumeSoon}
        className="flex overflow-x-auto no-scrollbar"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {[...brands, ...brands].map((brand, i) => (
          <div key={`${brand.name}-${i}`} className="w-40 shrink-0 mx-2">
            <BrandCard brand={brand} isDark={isDark} />
          </div>
        ))}
      </div>
    </div>
  );
}

/* =======================================
   Main HomePage Component
======================================= */
export default function HomePage() {
  const { isDark } = useTheme();
  const { products } = useProducts();
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.9]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);

  const featuredProducts = useMemo(
    () => products.filter((p) => p.featured && p.inStock).slice(0, 8),
    [products],
  );
  const showcaseProducts = useMemo(
    () => [
      products.find(p => p.brand === 'ZYN' && p.bestSeller),
      products.find(p => p.brand === 'VELO' && p.bestSeller),
      products.find(p => p.brand === 'Pablo' && p.bestSeller),
      products.find(p => p.brand === 'White Fox'),
      products.find(p => p.brand === 'VELO Shift'),
    ].filter(Boolean) as Product[],
    [products],
  );
  const veloShiftProducts = useMemo(
    () => products.filter(p => p.brand === 'VELO Shift'),
    [products],
  );
  // On/off switch for the "Introducing VELO Shift" homepage showcase.
  // Disabled for now (out of stock) — flip back to `true` to re-enable it.
  const SHOW_VELO_SHIFT = false;

  // "How to Use" mobile swipe carousel: track the centered card for the dots.
  const stepsRef = useRef<HTMLDivElement>(null);
  const [stepIdx, setStepIdx] = useState(0);
  const onStepsScroll = () => {
    const el = stepsRef.current;
    if (!el) return;
    const mid = el.getBoundingClientRect().left + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((c, i) => {
      const r = (c as HTMLElement).getBoundingClientRect();
      const d = Math.abs(r.left + r.width / 2 - mid);
      if (d < bestDist) { bestDist = d; best = i; }
    });
    setStepIdx(best);
  };
  const scrollToStep = (i: number) => {
    const el = stepsRef.current;
    const ch = el?.children[i] as HTMLElement | undefined;
    if (!el || !ch) return;
    // Align the chosen card's left edge to the container's left (snap-start).
    el.scrollTo({ left: el.scrollLeft + ch.getBoundingClientRect().left - el.getBoundingClientRect().left, behavior: 'smooth' });
  };

  const homeSeoJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'ZeroSmoke',
      url: 'https://zerosmoke.in',
      logo: 'https://zerosmoke.in/favicon.svg',
      description: 'India\'s premium online store for tobacco-free nicotine pouches.',
      contactPoint: { '@type': 'ContactPoint', contactType: 'customer service', availableLanguage: ['English', 'Hindi'] },
      sameAs: ['https://www.instagram.com/zerosmoke.in_2026'],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'ZeroSmoke.in',
      url: 'https://zerosmoke.in',
    },
  ];

  return (
    <div className="overflow-hidden">
      <SEO
        {...pageSeoMeta.home}
        jsonLd={homeSeoJsonLd}
      />
      {/* ===== HERO ===== */}
      <motion.section
        ref={heroRef}
        style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}
        className="hero-gradient relative min-h-screen flex items-center"
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-600/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1.5s' }} />
          <div className="absolute top-1/2 left-1/2 w-[300px] h-[300px] sm:w-[600px] sm:h-[600px] -translate-x-1/2 -translate-y-1/2 bg-emerald-500/5 rounded-full blur-3xl" />

          {/* Subtle grid pattern */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '60px 60px',
            }}
          />
        </div>

        {/* Seasonal campaign decoration (renders only when a campaign is live) */}
        <CampaignHeroDecor />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <CampaignOfferChip />

              <motion.span
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-400 text-sm font-medium mb-6 border border-emerald-500/20"
              >
                <motion.span
                  animate={{ rotate: [0, 15, -15, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <Sparkles size={14} />
                </motion.span>
                ZeroSmoke — Country&apos;s #1 Store for Authentic Imported Nicotine Pouches
              </motion.span>

              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold text-white leading-[1.1] mb-6">
                <motion.span
                  className="block"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  Clean Nicotine.
                </motion.span>
                <motion.span
                  className="block text-gradient"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                >
                  No Tobacco,
                </motion.span>
                <motion.span
                  className="block"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  No Smoke.
                </motion.span>
                <motion.span
                  className="block text-emerald-400"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.75 }}
                >
                  Just the Hit.
                </motion.span>
              </h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
                className="text-gray-400 text-lg max-w-lg mb-8 leading-relaxed"
              >
                100% authentic, original-market nicotine pouches — ZYN, VELO, CLEW, Pablo &amp; more. No fakes, no grey market. Just the real thing, delivered across Country.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="flex flex-wrap gap-4"
              >
                <Link
                  to="/store"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25 active:scale-[0.97]"
                >
                  Shop Now
                  <span className="group-hover:translate-x-1 transition-transform">
                    <ArrowRight size={18} />
                  </span>
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-8 py-4 border border-gray-600 text-gray-300 rounded-xl font-semibold hover:border-emerald-500 hover:text-emerald-400 transition-all hover:bg-emerald-500/5"
                >
                  Learn About ZeroSmoke
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.3 }}
                className="flex items-center gap-8 mt-10"
              >
                {[
                  { value: 50, suffix: '+', label: 'Products' },
                  { value: 5, suffix: '', label: 'Brands' },
                  { value: 1, suffix: 'K+', label: 'Happy Customers' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <div className="text-2xl font-bold text-white">
                      <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider">{stat.label}</div>
                  </div>
                ))}
                <div>
                  <div className="text-2xl font-bold text-white">4.8★</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Rating</div>
                </div>
              </motion.div>
            </motion.div>

            {/* Floating Product Orbit */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.5 }}
              className="hidden lg:block"
            >
              <FloatingProductOrbit />
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
        >
          <span className="text-xs text-gray-500 uppercase tracking-widest">Scroll</span>
          <ChevronDown size={20} className="text-gray-500" />
        </motion.div>
      </motion.section>

      {/* ===== TRUST STRIP ===== */}
      <section
        className={`relative z-10 border-y transition-colors duration-300 ${
          isDark ? 'bg-gray-900 border-gray-800' : 'bg-emerald-50 border-emerald-200'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          <ul
            className={`flex items-center justify-around gap-x-6 gap-y-3 flex-wrap text-sm sm:text-[15px] font-semibold ${
              isDark ? 'text-gray-100' : 'text-gray-800'
            }`}
          >
            {[
              { icon: <BadgeCheck className="w-5 h-5" />, label: '100% Authentic' },
              { icon: <Wallet className="w-5 h-5" />, label: 'COD Available' },
              { icon: <Truck className="w-5 h-5" />, label: 'Free Shipping ₹5,000+' },
              { icon: <MapPin className="w-5 h-5" />, label: 'Pan-Country 4–5 day delivery' },
            ].map((b) => (
              <li key={b.label} className="flex items-center gap-2">
                <span className="text-emerald-500 flex-shrink-0">{b.icon}</span>
                <span className="whitespace-nowrap">{b.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ===== VALUE PROPS ===== */}
      <section className={`py-10 sm:py-16 border-b border-t transition-colors duration-300 ${isDark ? 'bg-gray-950 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {[
              { icon: <Shield className="w-7 h-7" />, title: 'Smoke-Free', desc: 'No combustion, no vapor, no ash', color: 'from-emerald-400 to-emerald-600' },
              { icon: <Eye className="w-7 h-7" />, title: '100% Discreet', desc: 'Use anytime, anywhere unnoticed', color: 'from-blue-400 to-blue-600' },
              { icon: <Thermometer className="w-7 h-7" />, title: 'Climate Stored', desc: 'Temperature-controlled storage always', color: 'from-purple-400 to-purple-600' },
              { icon: <Award className="w-7 h-7" />, title: 'Most Reliable', desc: "Country's most trusted pouch store", color: 'from-amber-400 to-amber-600' },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                className="text-center group cursor-default"
              >
                <div className="flex justify-center mb-4">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    {item.icon}
                  </div>
                </div>
                <h3 className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED PRODUCTS ===== */}
      <section className={`relative py-12 sm:py-24 overflow-hidden transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Decorative background glows */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-16 left-1/4 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl translate-x-1/4 translate-y-1/4" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-8 sm:mb-12"
          >
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-xs font-semibold uppercase tracking-wider">
                <Sparkles size={13} />
                Curated For You
              </span>
              <h2 className={`text-3xl sm:text-5xl font-extrabold mt-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Featured <span className="text-gradient">Products</span>
              </h2>
              <p className={`mt-3 max-w-md text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Hand-picked favourites our customers keep coming back for.
              </p>
            </div>
            <Link
              to="/store"
              className={`group shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full border font-semibold text-sm transition-all ${
                isDark
                  ? 'border-gray-700 text-gray-200 hover:border-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/5'
                  : 'border-gray-200 text-gray-700 hover:border-emerald-500 hover:text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              View All
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {featuredProducts.map((product, i) => (
              <ProductCard key={product.id} product={product} index={i} />
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/store"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25 active:scale-[0.97]"
            >
              Shop Now
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ===== VELO SHIFT PREMIUM SHOWCASE (disabled until back in stock) ===== */}
      {SHOW_VELO_SHIFT && (
      <section className="relative py-16 sm:py-28 overflow-hidden">
        {/* Premium gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-950 via-gray-950 to-indigo-950" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(124,58,237,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(99,102,241,0.2) 0%, transparent 50%)' }} />
        {/* Animated grain overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\'/%3E%3C/svg%3E")' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', stiffness: 200, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6"
            >
              <Sparkles size={14} className="text-violet-400" />
              <span className="text-violet-300 text-xs font-semibold uppercase tracking-widest">Premium Collection</span>
              <Sparkles size={14} className="text-violet-400" />
            </motion.div>
            <h2 className="text-4xl sm:text-6xl font-extrabold text-white leading-tight">
              Introducing{' '}
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">
                VELO Shift
              </span>
            </h2>
            <p className="mt-5 text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
              The art of refined nicotine. Meticulously crafted for elegance and discretion,
              each pouch delivers smooth, balanced satisfaction — because your lifestyle
              deserves nothing less than exceptional.
            </p>
          </motion.div>

          {/* Product showcase */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-5">
            {veloShiftProducts.map((product, i) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -12, transition: { duration: 0.3 } }}
              >
                <Link
                  to={`/product/${product.id}`}
                  className="group block relative rounded-2xl overflow-hidden border border-violet-500/20 bg-gradient-to-b from-violet-900/30 to-gray-900/80 backdrop-blur-sm hover:border-violet-400/50 transition-all duration-500"
                >
                  {/* Glow effect on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-violet-600/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                  {/* Image */}
                  <div className="relative p-6 pb-2 flex items-center justify-center">
                    <div className="absolute inset-0 bg-gradient-to-b from-violet-500/5 to-transparent rounded-t-2xl" />
                    <img
                      src={product.image}
                      alt={`${product.name} nicotine pouches`}
                      className="w-36 h-36 object-contain drop-shadow-[0_10px_30px_rgba(124,58,237,0.3)] group-hover:scale-110 transition-transform duration-500 relative z-10"
                      loading="lazy"
                    />
                  </div>

                  {/* Info */}
                  <div className="p-5 pt-3 text-center relative z-10">
                    <span className="text-violet-400 text-[10px] font-bold uppercase tracking-widest">VELO Shift</span>
                    <h3 className="text-white font-bold text-sm mt-1 mb-2 group-hover:text-violet-200 transition-colors">
                      {product.flavor}
                    </h3>
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                      <span>{product.strength}{product.strengthUnit}</span>
                      <span>·</span>
                      <span>{product.format}</span>
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-2">
                      <span className="text-white font-bold text-lg">₹{product.price.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="mt-3 px-4 py-2 rounded-xl bg-violet-600/20 border border-violet-500/30 text-violet-300 text-xs font-semibold group-hover:bg-violet-600 group-hover:text-white group-hover:border-violet-600 transition-all duration-300">
                      View Product
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="text-center mt-12"
          >
            <Link
              to="/store?brand=VELO+Shift"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-violet-500 hover:to-indigo-500 transition-all hover:shadow-lg hover:shadow-violet-600/25 group"
            >
              Explore Full Collection
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>
      )}

      {/* ===== WHY OUR PRICES ===== */}
      <section className={`relative py-12 sm:py-24 overflow-hidden transition-colors duration-300 border-b ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200'}`}>
        {/* Decorative background blobs */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-600/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">

            {/* Left — Story */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">Why Our Prices</span>

              <h2 className={`text-3xl sm:text-5xl font-extrabold leading-tight mt-2 mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Authentic pouches cost more.{' '}
                <span className="text-gradient">Here&apos;s the honest reason.</span>
              </h2>

              <div className={`space-y-4 text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                <p>
                  The Country's market is filled with cheap, unverified stock — no proof of authenticity, no accountability. We don&apos;t sell that.
                </p>
                <p>
                  Every product we carry is sourced directly from original markets, not grey-market resellers or repackaged fakes. That&apos;s a real cost — and we pass it on honestly, without inflating it further.
                </p>
                <p>
                  When you buy from ZeroSmoke, you&apos;re paying for what the product actually costs to source genuinely. Nothing more.
                </p>
              </div>

              {/* Callout box */}
              <div className={`flex items-start gap-4 p-4 rounded-2xl border-l-4 border-l-emerald-500 ${isDark ? 'bg-emerald-500/5 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-100'}`}>
                <span className="text-emerald-500 shrink-0 mt-0.5"><Shield className="w-5 h-5" /></span>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <span className="font-bold">No compromise on authenticity.</span> Every batch is verified before it reaches you — or we don&apos;t ship it.
                </p>
              </div>
            </motion.div>

            {/* Right — 2×3 checklist grid */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: <BadgeCheck className="w-5 h-5" />, title: '100% Authentic', desc: 'Factory-sealed, never tampered' },
                { icon: <Shield className="w-5 h-5" />, title: 'Original Markets', desc: 'Sourced directly, not grey-market' },
                { icon: <Check className="w-5 h-5" />, title: 'Batch Verified', desc: 'Codes on every can you can check' },
                { icon: <Eye className="w-5 h-5" />, title: 'Zero Fakes', desc: 'No counterfeit stock — ever' },
                { icon: <Package className="w-5 h-5" />, title: 'Photos First', desc: 'Users may request an order preview before shipment.' },
                { icon: <MapPin className="w-5 h-5" />, title: 'Discreet Delivery', desc: 'Secure & Opaque packaging, pan-Country' },
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.4 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className={`rounded-2xl p-4 border cursor-default transition-colors ${isDark ? 'bg-gray-800 border-gray-700 hover:border-emerald-500/40' : 'bg-white border-gray-200 hover:border-emerald-300'}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`text-3xl font-black leading-none tracking-tight ${isDark ? 'text-emerald-400' : 'text-emerald-500'}`}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-md">
                      {item.icon}
                    </div>
                  </div>
                  <p className={`text-sm font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                  <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                </motion.div>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* ===== CAN ANIMATION ===== */}
      <section className={`py-12 sm:py-24 transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">Experience</span>
              <h2 className={`text-3xl sm:text-5xl font-extrabold mt-2 mb-6 leading-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Open a Can of
                <br />
                <span className="text-gradient">Pure Freshness</span>
              </h2>
              <p className={`text-lg leading-relaxed mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Every can holds 20 perfectly crafted nicotine pouches. Pop the lid,
                take one out, and experience the clean nicotine rush in seconds.
              </p>

              <div className="space-y-4">
                {[
                  { icon: <Package size={20} />, text: '20 pouches per can' },
                  { icon: <Zap size={20} />, text: 'Instant nicotine release in 5 min' },
                  { icon: <Heart size={20} />, text: 'No staining, no bad breath' },
                  { icon: <Shield size={20} />, text: '100% tobacco-free formula' },
                ].map((item, i) => (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600'}`}>
                      {item.icon}
                    </div>
                    <span className={`font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{item.text}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <div>
              {showcaseProducts.length > 0 && <CanAnimation showcaseProducts={showcaseProducts} />}
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW TO USE ===== */}
      <section className={`py-12 sm:py-24 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 sm:mb-20"
          >
            <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">Simple &amp; Easy</span>
            <h2 className={`text-3xl sm:text-5xl font-extrabold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Three Steps to <span className="text-gradient">Freedom</span>
            </h2>
            <p className={`mt-4 max-w-xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No lighter, no charger, no hassle. Just pop, place, and enjoy.
            </p>
          </motion.div>

          <div className="relative">
            {/* Connecting line between steps (desktop only) */}
            <div className="hidden md:block absolute top-24 left-[20%] right-[20%] h-0.5">
              <motion.div
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0.5, ease: 'easeInOut' }}
                className={`h-full origin-left ${isDark ? 'bg-gradient-to-r from-emerald-500/40 via-blue-500/40 to-amber-500/40' : 'bg-gradient-to-r from-emerald-300 via-blue-300 to-amber-300'}`}
              />
            </div>

            {/* Mobile: horizontal swipe carousel (snap); desktop: 3-up grid. */}
            <div
              ref={stepsRef}
              onScroll={onStepsScroll}
              className="flex md:grid md:grid-cols-3 gap-4 md:gap-8 mb-6 md:mb-16 overflow-x-auto md:overflow-visible snap-x snap-mandatory no-scrollbar pb-2 md:pb-0"
              style={{ WebkitOverflowScrolling: 'touch' }}
            >
              {HOW_TO_STEPS.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.15, duration: 0.5, ease: 'easeOut' }}
                  whileHover={{ y: -10, transition: { duration: 0.3 } }}
                  className={`relative snap-start shrink-0 w-[80%] sm:w-[48%] md:w-auto rounded-2xl md:rounded-3xl p-6 md:p-8 text-center border transition-all cursor-default group overflow-hidden ${
                    isDark
                      ? 'bg-gray-900/80 border-gray-800 hover:border-gray-600'
                      : 'bg-white border-gray-200 hover:border-gray-300 shadow-lg hover:shadow-2xl'
                  }`}
                >
                  {/* Glow behind icon on hover */}
                  <div className={`absolute top-8 left-1/2 -translate-x-1/2 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-30 transition-opacity duration-500 ${
                    item.bgGlow === 'emerald' ? 'bg-emerald-500' : item.bgGlow === 'blue' ? 'bg-blue-500' : 'bg-amber-500'
                  }`} />

                  <div className="relative z-10">
                    {/* Icon circle with gradient */}
                    <motion.div
                      className={`w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 md:mb-5 rounded-2xl bg-gradient-to-br ${item.gradient} flex items-center justify-center text-white shadow-lg`}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      {item.icon}
                    </motion.div>

                    {/* Step number badge */}
                    <div className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold mb-3 md:mb-4 ${
                      isDark ? 'bg-gray-800 text-gray-300 ring-1 ring-gray-700' : 'bg-gray-100 text-gray-600 ring-1 ring-gray-200'
                    }`}>
                      {item.step}
                    </div>

                    <h3 className={`text-lg md:text-xl font-bold mb-2 md:mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</h3>
                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Swipe dots — mobile only */}
            <div className="flex md:hidden items-center justify-center gap-2 mb-10">
              {HOW_TO_STEPS.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollToStep(i)}
                  aria-label={`Go to step ${i + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    stepIdx === i ? 'w-6 bg-emerald-500' : `w-2 ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`
                  }`}
                />
              ))}
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className={`rounded-2xl p-6 text-center max-w-2xl mx-auto border ${
              isDark ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-emerald-50 border-emerald-200'
            }`}
          >
            <p className={`font-medium ${isDark ? 'text-emerald-300' : 'text-emerald-800'}`}>
              <Leaf className="inline-block mr-2 mb-1" size={16} />
              <strong>First-Timer Tip:</strong> Start with a light or medium strength pouch. 
              You can always try stronger variants later!
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== BRANDS ===== */}
      <section className={`py-12 sm:py-24 transition-colors duration-300 ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10 sm:mb-16"
          >
            <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">Premium Selection</span>
            <h2 className={`text-3xl sm:text-5xl font-extrabold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Our <span className="text-gradient">Brands</span>
            </h2>
            <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              World-class nicotine pouch brands, curated for Country.
            </p>
          </motion.div>

          {/* Tablet / desktop: static grid */}
          <div className="hidden sm:grid grid-cols-3 lg:grid-cols-6 gap-6">
            {brands.map((brand, i) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -8 }}
              >
                <BrandCard brand={brand} isDark={isDark} />
              </motion.div>
            ))}
          </div>

          {/* Mobile: auto-scrolling + hand-swipeable marquee */}
          <BrandsMarquee isDark={isDark} />
        </div>
      </section>

      {/* ===== WHAT ARE NICOTINE POUCHES ===== */}
      <section className="py-12 sm:py-24 bg-gray-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-emerald-400 text-sm font-semibold uppercase tracking-wider">The Basics</span>
              <h2 className="text-3xl sm:text-5xl font-extrabold mt-2 mb-6 leading-tight">
                What Are Nicotine
                <br />
                <span className="text-gradient">Pouches?</span>
              </h2>
              <p className="text-gray-400 leading-relaxed mb-6">
                Nicotine Pouches are small, discreet pouches that contain nicotine and other
                ingredients but no tobacco. They are designed to be placed between the gum and lip,
                allowing users to absorb nicotine through the lining of the mouth.
              </p>
              <p className="text-gray-400 leading-relaxed mb-8">
                They come in various flavors and nicotine strengths, offering a smokeless and
                spit-free alternative to traditional tobacco products. Perfect for modern Country.
              </p>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: <Shield size={20} className="text-emerald-400" />, text: 'Tobacco-Free' },
                  { icon: <Leaf size={20} className="text-emerald-400" />, text: 'No Smoke, No Vapor' },
                  { icon: <Sparkles size={20} className="text-emerald-400" />, text: 'Multiple Flavors' },
                  { icon: <Check size={20} className="text-emerald-400" />, text: 'Use Anywhere' },
                ].map((item, i) => (
                  <motion.div
                    key={item.text}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                      {item.icon}
                    </div>
                    <span className="text-sm font-medium text-gray-300">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              <Link
                to="/store"
                className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25"
              >
                Explore Products <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* Comparison Table */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="text-xl font-bold mb-2">How Do They Compare?</h3>
              <p className="text-gray-400 text-sm mb-6">See why nicotine pouches are the modern choice</p>
              <div className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-800">
                      <th className="p-4 text-left text-gray-400 font-medium">Feature</th>
                      <th className="p-4 text-center text-gray-400 font-medium">Cigarettes</th>
                      <th className="p-4 text-center text-gray-400 font-medium">Chewing Tobacco</th>
                      <th className="p-4 text-center font-medium text-emerald-400">Nic Pouches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: 'Smoke-Free', cig: false, chew: true, pouch: true },
                      { feature: 'No Spitting', cig: true, chew: false, pouch: true },
                      { feature: 'Minimal Smell', cig: false, chew: false, pouch: true },
                      { feature: 'Tobacco-Free', cig: false, chew: false, pouch: true },
                      { feature: 'Use Anywhere', cig: false, chew: false, pouch: true },
                      { feature: '100% Discreet', cig: false, chew: false, pouch: true },
                    ].map((row, i) => (
                      <motion.tr
                        key={row.feature}
                        className="border-b border-gray-800/50"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <td className="p-4 text-gray-300">{row.feature}</td>
                        <td className="p-4 text-center">
                          <span className={`text-lg font-bold ${row.cig ? 'text-green-400' : 'text-red-400'}`} aria-label={row.cig ? 'Yes' : 'No'}>
                            {row.cig ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`text-lg font-bold ${row.chew ? 'text-green-400' : 'text-red-400'}`} aria-label={row.chew ? 'Yes' : 'No'}>
                            {row.chew ? '✓' : '✗'}
                          </span>
                        </td>
                        <td className="p-4 text-center">
                          <span className={`text-lg font-bold ${row.pouch ? 'text-emerald-400' : 'text-red-400'}`} aria-label={row.pouch ? 'Yes' : 'No'}>
                            {row.pouch ? '✓' : '✗'}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
                <div className="p-4 text-center">
                  <motion.span
                    className="text-emerald-400 text-sm font-semibold"
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    ✨ The clear winner for modern Country
                  </motion.span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS (Marquee) ===== */}
      <section className={`py-12 sm:py-24 transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8 sm:mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">Testimonials</span>
            <h2 className={`text-3xl sm:text-5xl font-extrabold mt-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Loved by <span className="text-gradient">Thousands</span>
            </h2>
            <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Real reviews from real customers across Country.
            </p>
          </motion.div>
        </div>

        <MarqueeReviews />
      </section>

      {/* ===== CTA BANNER ===== */}
      <section className="py-12 sm:py-24 hero-gradient relative overflow-hidden">
        {/* Animated shapes */}
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-10 left-10 w-32 h-32 border border-emerald-500/20 rounded-full"
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-48 h-48 border border-emerald-500/10 rounded-full"
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 5, repeat: Infinity }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 6, repeat: Infinity }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-5xl font-extrabold text-white mb-4">
              Ready to Make the{' '}
              <span className="text-gradient">Switch?</span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
              Join thousands choosing a smoke-free lifestyle. 
              Explore our complete range of nicotine pouches.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/store"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white rounded-xl font-semibold hover:bg-emerald-700 transition-all hover:shadow-lg hover:shadow-emerald-600/25 active:scale-[0.97]"
              >
                Shop Now
                <span className="group-hover:translate-x-1 transition-transform">
                  <ArrowRight size={18} />
                </span>
              </Link>
              <a
                href="https://www.instagram.com/zerosmoke.in_2026/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 border border-gray-600 text-gray-300 rounded-xl font-semibold hover:border-emerald-500 hover:text-emerald-400 transition-all hover:bg-emerald-500/5"
              >
                Follow Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
